import { useMemo, useState } from "react";
import { Stage, Layer, Rect, Line, Text, Circle } from "react-konva";
import { Avatar, Button, Card, CardBody, CardFooter, CardHeader, Chip, cn, Divider, Image, Radio, RadioGroup, ScrollShadow, Tab, Tabs } from "@heroui/react";
import { Icon } from "@iconify/react/dist/iconify.js";
import useWorkspaceBranding from "./hooks/useWorkspaceBranding"
import MainStore from "./MainStore";
import { SlideStructurePreview } from "./@components/SlideStructurePreview";
import VerticalSteps from "./@components/vertical-steps";
import { classNames, formatNumber } from "./SharedTransforms";

const ICON_MAP = {
  file: "lucide:file-text",
  "file-text": "lucide:file-text",
  clock: "lucide:clock-3",
  "check-circle": "lucide:circle-check-big",
  check: "lucide:circle-check-big",
  star: "lucide:star",
  certificate: "lucide:award",
  sparkles: "lucide:sparkles",
  lightning: "lucide:zap",
  repeat: "lucide:repeat-2",
  timer: "lucide:timer",
  users: "lucide:users",
  robot:"material-symbols:robot-2-outline",
  "news": "lucide:newspaper",
  "hockey-graph": "mdi:graph-line"
};


const TAB_DEFS = [
  { id: "overview", name: "Overview" },
  { id: "how", name: "How it Works" },
  { id: "when", name: "When to Use" },
  { id: "data", name: "Data Sources" },
];

function flattenReferenceParameters(flow) {
  const base = flow?.referenceParameters ?? {};
  const nested = base?.referenceParameters && typeof base.referenceParameters === "object"
    ? base.referenceParameters
    : {};
  return { ...nested, ...base };
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function safeObjectArray(value) {
  return Array.isArray(value) ? value.filter((entry) => entry && typeof entry === "object") : [];
}

export default function WorkflowOverview({ flow, onRun, onConfigure, onClose }) {
    const activeWorkspaceId = MainStore().activeWorkspaceId
    const workspaces = MainStore().workspaces?.() ?? []
    const activeWorkspace = useMemo(()=>{
        if( !activeWorkspaceId ){
            return undefined
        }
        return workspaces.find((workspace)=>{
            const matchId = workspace.id ?? workspace._id
            return `${matchId}` === `${activeWorkspaceId}`
        })
    }, [workspaces, activeWorkspaceId])
    const {
      workspaceGradient,
      workspaceGradientValues,
        accentColor,
        accentBaseClassName,
        accentPanelClassName,
        accentButtonClassName,
        accentTextClassName,
        accentBorderClassName,
        accentIndicatorClassName,
        secondaryColor,
        secondaryBaseClassName,
        secondaryPanelClassName,
        secondaryButtonClassName,
        secondaryTextClassName,
        secondaryBorderClassName,
        secondaryIndicatorClassName,
    } = useWorkspaceBranding(activeWorkspace)
  const [activeSlideIdx, setActiveSlideIdx] = useState(0)

  const reference = flattenReferenceParameters(flow);

  const oneLiner = reference.one_line ?? reference.subtitle;
  const description = reference.description ?? reference.summary;

  const bestForItems = safeArray(reference.best_for);
  const inputItems = safeArray(reference.inputs);
  const outputs = reference.outputs;
  const outputItems = safeArray(reference.outputs?.details);
  const deliverables = safeObjectArray(reference.whats_included);
  const inclusionItems = safeObjectArray(reference.workflow_includes);
  const whenToUseItems = safeArray(reference.when_to_use_this_workflow);
  const considerOtherItems = safeObjectArray(reference.when_to_consider_others);
  const dataSourceItems = safeObjectArray(reference.data_sources);
  const canConfigure = safeObjectArray(reference.can_configure);
  const dataVolume = safeObjectArray(reference.data_volume);
  const steps = safeObjectArray(reference.steps);
  const quality = safeObjectArray(reference.data_quality);
  const tech = safeObjectArray(reference.tech_stack);
  const examples = safeObjectArray(reference.examples ?? reference.use_cases);

  const outputSummary = outputs?.summary ?? (Array.isArray(outputs) ? outputs[0] : undefined)

  const metrics = [
    reference.time ? { label: "Turnaround", value: reference.time, icon: ICON_MAP.clock } : null,
    outputSummary ? { label: "Output", value: outputSummary, icon: ICON_MAP.file } : null,
    reference.credits !== undefined && reference.credits !== null
      ? { label: "Credits", value: `${reference.credits}`, icon: ICON_MAP.sparkles }
      : null,
    /*reference.typical_runs_per_month
      ? { label: "Typical runs/mo", value: reference.typical_runs_per_month, icon: ICON_MAP.repeat }
      : null,
    reference.team_runs
      ? { label: "Team runs", value: reference.team_runs, icon: ICON_MAP.users }
      : null,*/
    reference.rating
      ? { label: "Rating", value: reference.rating, icon: ICON_MAP.star }
      : null,
  ].filter(Boolean);

  const flowSteps = useMemo(() => {
    if (inclusionItems.length > 0) {
      return inclusionItems.map((item) => ({ label: item.title }));
    }

    const originSteps = flow?.primitives?.origin?.allUniqueItems ?? flow?.primitives?.origin ?? [];
    if (Array.isArray(originSteps) && originSteps.length > 0) {
      return originSteps
        .filter((step) => step?.type && step.type !== "view" && step.type !== "page")
        .slice(0, 5)
        .map((step) => ({
          label: step.configParent?.title ?? step.getConfig?.labelForMap ?? step.title ?? step.type,
        }));
    }
    return [];
  }, [flow, inclusionItems]);
  
  const slides = useMemo(()=>{
    function collect(d, topLevel = false){
      const direct = d.primitives.origin.allPage
      const subflows = d.primitives.origin.allFlow
      return [
        ...direct.flatMap(d=>({topLevel, ...d})),
        ...subflows.flatMap(d=>collect(d)),
      ]
    }
    return [{id:"cover", title: flow.title, description: flow.referenceParameters.description}, ...collect(flow)]

  }, [flow])

  const [activeTab, setActiveTab] = useState("overview");
  if (!flow) {
    return null;
  }

  const handleTabChange = (next) => {
    const value = `${next ?? ""}`.trim().toLowerCase();
    const normalized = value.replace(/\s+/g, "");
    const match = TAB_DEFS.find((tab) => {
      const tabId = tab.id.toLowerCase();
      const nameLower = tab.name.toLowerCase();
      return (
        tabId === value ||
        tabId === normalized ||
        nameLower === value ||
        nameLower.replace(/\s+/g, "") === normalized
      );
    });
    setActiveTab(match?.id ?? "overview");
  };

  const resolveIcon = (name, fallback = "lucide:sparkles") => {
    if (!name || typeof name !== "string") {
      return fallback;
    }
    const key = name.toLowerCase();
    return ICON_MAP[key] ?? ICON_MAP[name] ?? `lucide:${key}`;
  };

  const idString = `${flow?.id ?? flow?.plainId ?? flow?.title ?? "workflow"}`;
  const gradientCharCode = idString.length > 0 ? idString.charCodeAt(0) : 0;
  const safeGradientIndex = Number.isFinite(gradientCharCode) ? gradientCharCode : 0;

  const flowTitle = flow?.title ?? reference.title ?? "Untitled Workflow";

  const activeSlide = slides[activeSlideIdx];

  return (
    <div className="w-full max-w-7xl space-y-3">
          <div className="flex items-start justify-between gap-4">
            <div className="space-y-1">
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-slate-400">Workflow</p>
              <h2 className="text-2xl font-semibold text-slate-900">{flowTitle}</h2>
              {oneLiner && <p className="text-sm text-slate-600">{oneLiner}</p>}
            </div>
            {onClose && (
              <button
                type="button"
                onClick={onClose}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 text-slate-500 transition hover:border-slate-300 hover:text-slate-700"
              >
                <Icon icon="lucide:x" className="h-4 w-4 shrink-0 " />
              </button>
            )}
          </div>
      <div className="lg:grid gap-8 xl:gap-1- lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)]">
        <div className="space-y-4">
          <div
            className={`relative flex min-h-[240px] flex-col overflow-hidden rounded-3xl border ${accentBorderClassName} max-w-100`}
            style={{ backgroundImage: workspaceGradient, backgroundSize: "cover" }}
          >
            <div className="absolute inset-0 bg-background/75" />
            {activeSlide && (
              <div className="relative z-10 mt-3 flex flex-1 flex-col gap-4">
                <div className="flex justify-center w-full h-56 xl:h-64 p-2">
                  {activeSlide.id === "cover" && <Image 
                        alt="Card background"
                        className="z-0 w-full h-full object-cover aspect-video"
                        src={`/api/image/${flow.id}`} />}
                  {activeSlide.id !== "cover" && <SlideStructurePreview
                    className="h-full w-full"
                    slide={activeSlide}
                    enableResizeObserver={true}
                    gradientValues={workspaceGradientValues}
                    stroke={accentColor}
                  />}
                </div>
                  <div className="flex flex-col bg-background/50 p-3 min-h-20">
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                        {activeSlide.referenceParameters?.overview ?? activeSlide.title}
                      </p>
                    {activeSlide.referenceParameters?.description && (
                      <p className="text-sm font-light text-slate-600">
                        {activeSlide.referenceParameters?.description}
                      </p>
                    )}
                  </div>
              </div>
            )}
          </div>
            <ScrollShadow orientation="horizontal" hideScrollBar={true}>
            <RadioGroup 
              orientation="horizontal"
              classNames={{
                wrapper:"flex w-full flex-nowrap"
              }}
                value={activeSlide.id} 
                onValueChange={(id)=>setActiveSlideIdx(Math.max(0, slides.findIndex(d=>d.id === id)))}>
                {slides.map((slideItem, index) => (
                  <Radio 
                    key={slideItem.id} 
                    value={slideItem.id}
                    classNames={{
                      wrapper: "hidden",
                      base: cn(
                        "inline-flex m-0 bg-content1 hover:bg-content2 items-center justify-between",
                        "flex-row-reverse max-w-[300px] cursor-pointer rounded-lg gap-4 p-2 border-2 border-transparent",
                        "data-[selected=true]:border-primary",
                        activeSlide?.id === slideItem.id ? accentBorderClassName : undefined
                      ),
                  }}
                    >
                    <div className="flex flex-col space-y-0.5 max-w-40 w-40">

                      <div className="p-1.5 rounded border border-slate-300 bg-default/30 ">
                        {slideItem.id === "cover" && <Image 
                              alt="Card background"
                              className="z-0 w-40 object-cover aspect-video"
                              src={`/api/image/${flow.id}`} />}
                        {slideItem.id !== "cover" && <SlideStructurePreview
                          key={slideItem.id ?? slideItem._id ?? index}
                          className="aspect-video max-w-40 w-40 shrink-0"
                          slide={slideItem}
                          shadowEnabled={false}
                          gradientValues={workspaceGradientValues}
                          stroke={accentColor}
                          />}
                      </div>
                      <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500 truncate overflow-hidden text-ellipsis">
                        {slideItem.title}
                      </p>
                    </div>
                  </Radio>
                ))}
              </RadioGroup>
            </ScrollShadow>
        </div>

        <div className="flex flex-col gap-5">

          {metrics.length > 0 && (
            <div className="grid gap-3 sm:grid-cols-3 bg-slate-50/70 border border-slate-200 rounded-2xl py-2">
              {metrics.map((metric, idx) => (
                <div
                  key={metric.label}
                  className={cn(
                    "flex items-center px-3 py-2 space-x-1",
                    idx > 0 && "border-l border-slate-200"
                  )}
                >
                  <div className={`flex h-6 w-6 items-center justify-center ${accentTextClassName}`}>
                    <Icon icon={metric.icon ?? ICON_MAP.sparkles} className="h-5 w-5" />
                  </div>
                  <div>
                    <p className="text-xxs uppercase tracking-wide text-slate-500">{metric.label}</p>
                    <p className="text-sm font-semibold text-slate-500">{metric.value}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
          {description && (
            <p className={`font-light text-slate-600 border-l-3 pl-3 ${accentBorderClassName}`}>
              {description}
            </p>
          )}

        <Divider/>
        <div className="flex flex-col">
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">This workflow includes</p>
          {inclusionItems.length === 0 && (
            <p className="mt-3 text-sm text-slate-500">No workflow steps documented yet.</p>
          )}
          <div className="mt-4 overflow-y-auto pr-1 grid sm:grid-cols-2">
            {inclusionItems.map((item, index) => (
              <div
                key={`${item.title ?? index}-${index}`}
                className="flex place-items-center gap-3 p-1"
              >
                  <Icon 
                    icon={resolveIcon(item.icon)} 
                    className={classNames(
                        "h-4 w-4 shrink-0",
                      item.icon === "check" ?  "text-slate-500" : accentTextClassName,
                  )} />
                  <p className="text-sm text-slate-600">{item.title ?? "Step"}</p>
              </div>
            ))}
          </div>
        </div>


          <div className="mt-auto flex flex-wrap gap-3">
            <Button
              color="primary"
              radius="sm"
              className={`h-11 min-w-[140px] justify-center px-5 text-sm font-semibold shadow-sm grow ${accentButtonClassName}`}
              startContent={<Icon icon="lucide:play" className="w-5 h-5" />}
              onPress={() => onRun?.(flow)}
            >
              Run Workflow
            </Button>
            <Button
              variant="flat"
              radius="sm"
              className={`h-11 min-w-[140px] justify-center px-5 text-sm font-semibold grow-0 ${secondaryButtonClassName}`}
              onPress={() => onConfigure?.(flow)}
            >
              Configure First
            </Button>
          </div>
        </div>

      </div>

      <div className="space-y-6">
        <Tabs
          aria-label="Workflow overview tabs"
          variant="underlined"
          selectedKey={activeTab}
          onSelectionChange={handleTabChange}
          classNames={{
            base: "w-full overflow-x-auto",
            tabList: "gap-4",
            tab: [
              "px-3 py-2 text-sm font-semibold transition-colors",
              "text-slate-500 data-[hover=true]:text-slate-700",
              accentTextClassName ? `data-[selected=true]:${accentTextClassName}` : "data-[selected=true]:text-slate-900",
            ].join(" "),
            cursor: accentIndicatorClassName ?? "bg-primary",
          }}
        >
          {TAB_DEFS.map((tab) => (
            <Tab key={tab.id} title={tab.name} />
          ))}
        </Tabs>

        <div className="px-4">
          {activeTab === "overview" && (
            <div className="space-y-6">
              <div className="space-x-6 flex w-full ">
                <div className="w-full px-2 ">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">What you&apos;ll get</p>
                  {deliverables.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">No deliverables documented yet.</p>
                  ) : (
                    <div className="mt-3 flex flex-col space-y-4">
                      {deliverables.map((item, index) => (
                        <div
                          key={`${item.title ?? index}-${index}`}
                          className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4"
                        >
                          <div className={`flex h-10 w-10 items-center justify-center rounded-lg ${accentPanelClassName} ${accentTextClassName ?? "text-default"} shadow-sm`}>
                            <Icon icon={resolveIcon(item.icon)} className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-600">{item.title ?? "Deliverable"}</p>
                            {item.description && <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="w-full px-2">
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">WHAT YOU CAN CONFIGURE </p>
                  {canConfigure.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">No configurations for this workflow.</p>
                  ) : (
                    <div className={`mt-3 text-sm space-y-3 p-3 lg:p-5 lg:space-y-5 rounded-lg ${secondaryPanelClassName} ${secondaryBorderClassName} border`}>
                      {canConfigure.map((item, index) => (
                        <div
                          key={`${item.need ?? index}-${index}`}
                          className="flex space-x-2"
                        >
                          <Icon icon={resolveIcon(item.icon, "lucide:arrow-right")} className={`mt-1 h-4 w-4 shrink-0 ${accentTextClassName ?? "text-primary"}`} />
                          <div className="w-full flex flex-col space-x-1">
                            <div className="flex space-x-1 place-items-center">
                              <p className="text-slate-500 font-semibold">{item.title}</p>
                              {item.cost && <Icon icon="ion:ticket-sharp" className={`h-4 w-4 shrink-0 ${accentTextClassName ?? "text-primary"}`} />}
                            </div>
                            <p className="text-slate-500 text-xs">{item.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div>
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Best for</p>
                {bestForItems.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">No recommendations documented yet.</p>
                ) : (
                  <div className="mt-3 grid gap-4 lg:grid-cols-2 xl:grid-cols-4">
                    {bestForItems.map((item, index) => (
                      <div
                        key={`${item.title ?? index}-${index}`}
                        className={`flex p-3 md:p-6 flex-col items-center gap-1.5 rounded-2xl border border-slate-200  ${secondaryPanelClassName} p-4`}
                      >
                        <div className={`flex h-10 w-10 mt-1 bb-3 items-center justify-center rounded-lg bg-white ${accentTextClassName ?? "text-default"} shadow-sm`}>
                          <Icon icon={resolveIcon(item.icon)} className="h-5 w-5" />
                        </div>
                        <p className="font-semibold text-sm mt-2 text-center text-slate-600">{item.title ?? "Deliverable"}</p>
                        {item.description && <p className="text-center text-xs text-slate-500">{item.description}</p>}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === "how" && (
              <div className="w-full flex">
                <div className="w-full pr-6">
                <VerticalSteps
                  markComplete = {false}
                  colorBase={`${accentBorderClassName} ${accentTextClassName}`}
                  colorLine={accentBaseClassName}
                  stepClassName="!py-0"
                  defaultStep ={steps.length}
                  steps={steps.map(d=>({
                    description: <Card shadow="none">
                      <CardHeader className="pb-0 pt-2 px-4 flex-col items-start space-y-0.5">
                        <p className="text-sm text-default-600 font-semibold">{d.title}</p>
                        <p className="text-xs text-default-500">{d.subtitle}</p>
                      </CardHeader>
                      <CardBody className="overflow-visible py-2 -mt-2">
                        <ul className="my-1 px-0.5 space-y-1 text-xs text-slate-600">
                          {(d.details ?? []).map((d, index) => (
                            <li key={`${d}-${index}`}>• {d}</li>
                          ))}
                        </ul>
                        <div className={`flex w-fit mt-1.5 px-1 py-0.5 rounded-lg border space-x-1 place-items-center ${accentBorderClassName} ${accentPanelClassName}`}>
                          <Icon icon="lucide:clock-3" className={`w-4 h-4 ${accentTextClassName}`}/><p className="text-xs text-default-500">{d.duration}</p>
                          </div>
                      </CardBody>

                    </Card>
                  }))}
                />
              </div>
              <div className="space-y-6 w-[40%] flex flex-col">
                <div className={`w-full border rounded-2xl border-slate-200 p-3 ${secondaryPanelClassName}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Inputs we need</p>
                  {inputItems.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">No inputs documented yet.</p>
                  ) : (
                    <ul className="mt-3 space-y-2 text-sm text-slate-600 list-disc list-outside pl-2 ml-0.5">
                      {inputItems.map((item, index) => (
                        <li key={`${item.title}-${index}`}>{item.title}{!item.optional && <Chip size="sm" className={`${accentButtonClassName} ml-1.5`}>Required</Chip>}</li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className={`w-full border rounded-2xl border-slate-200 p-3 ${secondaryPanelClassName}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Workflow outputs</p>
                  {outputItems.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">No outputs documented yet.</p>
                  ) : (
                    <div className="mt-3 text-sm space-y-3">
                      {outputItems.map((item, index) => (
                        <div
                          key={`${item.type}-${index}`}
                          className="flex space-x-2 place-items-center"
                        >
                          <Icon icon={item.icon ?  resolveIcon(item.icon ) : "lucide:circle-check-big"} className={`h-4 w-4 shrink-0 ${accentTextClassName ?? "text-primary"}`} />
                          <p className="text-slate-600">{item}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                {false && <div className={`w-full border rounded-2xl border-slate-200 p-3 ${secondaryPanelClassName}`}>
                  <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">TYPICAL DATA VOLUME</p>
                  {tech.length === 0 ? (
                    <p className="mt-3 text-sm text-slate-500">No metrics for this workflow.</p>
                  ) : (
                    <div className="mt-3 text-sm space-y-3">
                      {tech.map((item, index) => (
                        <div
                          key={`${item.type}-${index}`}
                          className="flex space-x-2 place-items-center"
                        >
                          <Icon icon={resolveIcon(item.icon ?? "file-text")} className={`h-4 w-4 shrink-0 ${accentTextClassName ?? "text-primary"}`} />
                          <p className="text-slate-600">{item.title}</p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>}
              </div>
            </div>
          )}

          {activeTab === "when" && (
            <>
            <div className="space-x-6 flex w-full">
              <div className="w-full">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Use this workflow for</p>
                {whenToUseItems.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">No recommendations documented yet.</p>
                ) : (
                    <div className="mt-3 flex flex-col space-y-4">
                      {whenToUseItems.map((item, index) => (
                        <div
                          key={`${item.title ?? index}-${index}`}
                          className="flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4"
                        >
                          <div className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${accentPanelClassName} ${accentTextClassName ?? "text-default"} shadow-sm`}>
                            <Icon icon={resolveIcon(item.icon)} className="h-5 w-5" />
                          </div>
                          <div>
                            <p className="text-sm font-semibold text-slate-600">{item.title}</p>
                            {item.description && <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.description}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                )}
              </div>

              {<div className="w-full">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Consider other workflows for</p>
                {considerOtherItems.length === 0 ? (
                  <p className="mt-3 text-sm text-slate-500">No guidance documented yet.</p>
                ) : (
                    <div className="mt-3 flex flex-col space-y-4">
                      {considerOtherItems.map((item, index) => (
                        <div
                          key={`${item.title ?? index}-${index}`}
                          className={`flex items-start gap-3 rounded-2xl border border-slate-200 hover:${secondaryBorderClassName} bg-white p-4 group hover:${secondaryPanelClassName}`}
                        >
                          <div className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${secondaryPanelClassName} ${secondaryTextClassName ?? "text-default"} shadow-sm`}>
                            <Icon icon={resolveIcon(item.icon)} className="h-5 w-5 " />
                          </div>
                          <div className="w-full grow">
                            <p className="text-sm font-semibold text-slate-600 group-hover:text-slate-800">{item.title}</p>
                            {item.description && <p className="mt-1 text-xs leading-relaxed text-slate-500">{item.description}</p>}
                          </div>
                          <div className="w-fit shrink-0 flex">
                            <Icon icon="lucide:chevron-right" className={`w-full shrink-0 ${secondaryTextClassName}`}/>
                          </div>
                        </div>
                      ))}
                    </div>
                )}
              </div>}
            </div>
              <div className="mt-6 flex flex-col w-full space-y-4">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">Example uses</p>
                <div className="grid md:grid-cols-2 gap-4 ">
                  {examples.map((item, index) => (
                    <Card isFooterBlurred key={`${item}-${index}`} className="w-full aspect-video">
                      <CardHeader className="flex-col items-start absolute z-10 bg-black/50">
                        <Icon icon={resolveIcon(item.icon)} className={`h-4 w-4 shrink-0 mr-1 text-white/90`} />
                        <span className="font-bold text-white text-lg">{item.title}</span>
                      <p className="text-white text-tiny">{item.description}</p>
                      </CardHeader>
                      <Image
                        removeWrapper
                        alt="Card background"
                        className="z-0 w-full h-full object-cover"
                        src="https://heroui.com/images/card-example-4.jpeg"
                      />
                      <CardFooter className="absolute bg-white/30 bottom-0 border-t-1 border-zinc-100/50 z-10 justify-between space-x-2">
                        <div className="space-y-1">
                          <p className="text-white text-sm font-semibold">{item.output}</p>
                        </div>
                        <div className="flex flex-col space-y-2 align-center">
                          <Button className={accentButtonClassName} radius="full" size="sm">
                            View
                          </Button>
                        </div>
                      </CardFooter>
                    </Card>
                  ))}
                </div>
              </div>
            </>
          )}

          {activeTab === "data" && (
            <div className="flex space-x-6">
              <div className="w-full">
                <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">DATA SOURCES BY FOCUS</p>
                {dataSourceItems.filter(d=>!d.secondary).length === 0 ? (
                  <p className="text-sm text-slate-500">No data sources documented yet.</p>
                ) : (
                    <div className="mt-3 grid md:grid-cols-2 gap-6">
                      {dataSourceItems.filter(d=>!d.secondary).map((item, index) => (
                        <div
                          key={`${item.title ?? index}-${index}`}
                          className={`flex items-start gap-3 rounded-2xl border border-slate-200 bg-white p-4`}
                        >
                          <div className={`flex h-10 w-10 items-center justify-center rounded-lg shrink-0 ${accentPanelClassName} ${accentTextClassName ?? "text-default"} shadow-sm`}>
                            <Icon icon={resolveIcon(item.icon)} className="h-5 w-5 " />
                          </div>
                          <div className="w-full grow">
                            <p className="text-sm font-semibold text-slate-600">{item.title}</p>
                            <ul className="text-xs space-y-1 text-slate-600 list-disc list-outside pl-2 ml-0.5">
                              {item.details?.map((item, index) => (
                                <li key={index}>{item}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


const sourceMap = {
  "google_news": {type: "company", domain: "google.com"},
  "google_scholar": {type: "company", domain: "google.com"},
  "reddit": {type: "company", domain: "reddit.com"},
  "linkedin": {type: "company", domain: "linkedin.com"},
  "press_releases": {type: "icon", icon: "lucide:newspaper"},
  "web": {type: "icon", icon: "lucide:newspaper"},
}

function mapSources(sources){
  if( !sources ){
    return []
  }
  return sources.map(d=>sourceMap[d]).filter(Boolean)
}
